import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { SectionData } from '@/components/sections/SectionRenderer';

export interface CustomPageData {
  id: string;
  title: string;
  slug: string;
  meta_description: string | null;
  is_published: boolean;
}

export interface CustomPageResult {
  page: CustomPageData;
  sections: SectionData[];
}

/**
 * Loads a published custom CMS page (by org + slug) together with its visible
 * sections, ordered by sort_order.
 *
 * Returns null when no published page matches, which the consuming component
 * treats as a not-found → <Navigate to="/404" />.
 */
export function useCustomPage(orgId: string | undefined, slug: string | undefined) {
  return useQuery({
    queryKey: ['custom-page', orgId, slug],
    enabled: Boolean(orgId) && Boolean(slug),
    queryFn: async (): Promise<CustomPageResult | null> => {
      const { data: pageData, error: pageError } = await supabase
        .from('custom_pages')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('slug', slug!)
        .eq('is_published', true)
        .maybeSingle();

      if (pageError) throw pageError;
      if (!pageData) return null;

      const { data: sectionsData } = await supabase
        .from('page_sections')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('page_id', pageData.id)
        .eq('is_visible', true)
        .order('sort_order');

      const sections: SectionData[] = (sectionsData ?? []).map((s) => ({
        ...s,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        content: (s.content || {}) as Record<string, any>,
        layout_variant: s.layout_variant || 'default',
      }));

      return { page: pageData, sections };
    },
  });
}
